import React from 'react';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { graphql, useStaticQuery } from 'gatsby';
import { IStaticContentfulImages } from 'src/types/contentful';

export interface IMasterclassArticle {
  heroBannerData: {
    title: string;
    label: string;
    wistiaVideoId: string;
    shortDescription: string;
  };
  articleBodyData: {
    content: React.ReactNode;
    label: string;
    title: string;
  };
  getTheGuideData?: {
    guideThumbnail: IGatsbyImageData;
    description: string;
    resourceUrl: string;
  };
  allSessionsData: {
    title: string;
    thumbnail?: IGatsbyImageData;
    description?: string;
    slug: string;
  };
}

type IMasterclassData = Record<string | number, IMasterclassArticle>;

export const getMasterclassAllSessionsDataByIndex = (
  index: number | string,
  articles: IMasterclassData
): IMasterclassArticle[] => {
  const articlesEntries = Object.entries(articles);
  const isCurrentArticle = (key: string) => key === index.toString();
  let positionsToJump = 0;

  return articlesEntries.reduce((acc, [key, value]) => {
    // Always exclude itself and the introduction
    if (isCurrentArticle(key) || key === 'introduction') return acc;

    if (!isNaN(Number(index)) && Number(key) > Number(index)) {
      acc.splice(positionsToJump, 0, value);

      positionsToJump++;
    } else {
      acc.push(value);
    }

    return acc;
  }, []);
};

const useMasterclassData = (): IMasterclassData => {
  const {
    introductionThumbnail,
    session1Thumbnail,
    session2Thumbnail,
    session3Thumbnail,
    session4Thumbnail,
    session5Thumbnail,
    session6Thumbnail,
    session7Thumbnail,
    session8Thumbnail,
    session9Thumbnail,
    sessionIntroCover,
    session1Cover,
    session2Cover,
    session3Cover,
    session4Cover,
    session5Cover,
    session6Cover,
    session7Cover,
    session8Cover,
    session9Cover,
  } = useStaticQuery<IStaticContentfulImages>(graphql`
    {
      introductionThumbnail: contentfulAsset(contentful_id: { eq: "5Tdisy9ww7Lgxs9kVVbKBK" }) {
        gatsbyImageData(width: 1200, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session1Thumbnail: contentfulAsset(contentful_id: { eq: "10YNOPjNk4CuIDwja7XaJA" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session2Thumbnail: contentfulAsset(contentful_id: { eq: "2lPgZOwj5TwH5MDvQDJ5wu" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session3Thumbnail: contentfulAsset(contentful_id: { eq: "7EqxSkMwAnaBsJzWd4T0YY" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session4Thumbnail: contentfulAsset(contentful_id: { eq: "5KLQnkuECsARkqhun93C1D" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session5Thumbnail: contentfulAsset(contentful_id: { eq: "7e1oylmnSTtgyPcj3wHB6c" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session6Thumbnail: contentfulAsset(contentful_id: { eq: "4M8jU2Ik86h70IxrkzTiNM" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session7Thumbnail: contentfulAsset(contentful_id: { eq: "3WMjq0DZIf5p7WkNyV6oEC" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session8Thumbnail: contentfulAsset(contentful_id: { eq: "GHMOdwKLPiXWicBWteZby" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session9Thumbnail: contentfulAsset(contentful_id: { eq: "26J7DJiJMJBdsZ0W63ZkuN" }) {
        gatsbyImageData(width: 950, layout: CONSTRAINED, placeholder: BLURRED)
      }

      sessionIntroCover: contentfulAsset(contentful_id: { eq: "3qXCYuadqSzGGmK4Vb8hvc" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session1Cover: contentfulAsset(contentful_id: { eq: "25u1CuU05OzlHHeiJxTwR5" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session2Cover: contentfulAsset(contentful_id: { eq: "2IudKmags62bppgwTclKUi" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session3Cover: contentfulAsset(contentful_id: { eq: "2ngTC4wh4d9fsWtiOylTkJ" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session4Cover: contentfulAsset(contentful_id: { eq: "7ahhxTsIJfOadMqPHCTUc8" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session5Cover: contentfulAsset(contentful_id: { eq: "2zAOjcmOUnbxhsX609Bg4I" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session6Cover: contentfulAsset(contentful_id: { eq: "78U39QgApKWQyEq9egi4Bz" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session7Cover: contentfulAsset(contentful_id: { eq: "6VR4pJgu7ZvtZnQZdAJR0z" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session8Cover: contentfulAsset(contentful_id: { eq: "5W01dj6qftC2IeyIGe9M95" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      session9Cover: contentfulAsset(contentful_id: { eq: "3gZRC25MF9v2wB314hMU8I" }) {
        gatsbyImageData(width: 1500, layout: CONSTRAINED, placeholder: BLURRED)
      }
    }
  `);

  return {
    introduction: {
      heroBannerData: {
        title: 'Welcome to People Magic Profit',
        label: 'Introduction',
        wistiaVideoId: 't2qdgtu4jl',
        shortDescription:
          'You CAN create a $1M digital business by bringing people together. The result is incredible transformations for them—and freedom, purpose, and profit for you.',
      },
      articleBodyData: {
        content: (
          <>
            <p className='ast-p-element'>
              People are flocking from social media to private communities. We want to spend time with people who are on
              the same path—and we’re willing to pay to do so if it gives us the focus and results we’re seeking.{' '}
            </p>
            <p className='ast-p-element'>
              What’s more, advanced technology (including, yes—AI) has made it easier to connect and collaborate with
              strangers who are coming together in pursuit of a shared goal or passion.
            </p>
            <p className='ast-p-element'>
              In this FREE 9-part masterclass you’ll learn exactly how to harness the momentum around community to build
              a business you can be proud of—and one that can essentially run itself.
            </p>
          </>
        ),
        label: 'Summary',
        title: 'There is no better time to build community',
      },
      getTheGuideData: {
        guideThumbnail: sessionIntroCover.gatsbyImageData,
        description: `It's complete with the breakthrough $1M Math Spreadsheet and includes the perfect path for you to get your first $1M.`,
        resourceUrl: 'https://docs.google.com/document/u/2/d/1AhCK4gia_3MRgBUgi1Vz3l4-Es7R3jK1rEeS3gUOQQ0/',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: introductionThumbnail.gatsbyImageData,
        description: `You CAN create a $1M digital business by bringing people together. \n \n The result is incredible transformations for them—and freedom, purpose, and profit for you. `,
        slug: 'intro',
      },
    },

    1: {
      heroBannerData: {
        title: 'Your Ideal Member',
        label: 'Session 1',
        wistiaVideoId: 'qg7ueiy4d1',
        shortDescription:
          'We’ll teach you how to identify and attract the person who needs and wants what you have to offer right now.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'The key to your Ideal Member is a transition',
        content: (
          <p className='ast-p-element'>
            The people who join paid communities, buy online courses, participate in challenges—they all have one thing
            in common. They are in a transition. That could be new dads, people entering the workforce for the first
            time, couples buying their first homes, someone moving to a new country...there are infinite transitions.
            Learn how to harness them as the first step top building a $1M community.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session1Cover.gatsbyImageData,
        description: 'Get the breakthrough formula for your Ideal Member, worksheets, and examples to inspire you.',
        resourceUrl: 'https://docs.google.com/document/d/1Owo9UniOVEjd_Cs9bM-IKOXLL85eETW0eD-4I74UsK0/',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session1Thumbnail.gatsbyImageData,
        description: `This session has gone viral from one simple message. DON’T choose a niche.\n\nDo this INSTEAD—pick a person in a transition. Then, bring them together in a community.`,
        slug: 'idealmember',
      },
    },

    2: {
      heroBannerData: {
        title: 'The Best Year Ever',
        label: 'Session 2',
        wistiaVideoId: '7ja9s7s1o4',
        shortDescription: 'Once you know who you want to bring together, you get to plan their best year ever.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'The Best Year Ever is your north star',
        content: (
          <p className='ast-p-element'>
            Picture the people in your community a year from now. What can they do? What have they done already that
            they couldn’t have done a year ago? We’re going to give you the only formula you’ll need to unlock exactly
            what your members will be able to do—thanks to your community.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session2Cover.gatsbyImageData,
        description:
          'It has a guided worksheet to help you brainstorm the BEST YEAR EVER for your members and hundreds of results to swipe.',
        resourceUrl: 'https://docs.google.com/document/d/1l7pbvWC7-hPtwH0iOv4MQKnyJ3jrcNpcVT6x2LaPLho/',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session2Thumbnail.gatsbyImageData,
        description: `The BEST YEAR EVER are the results and transformation your IDEAL MEMBER wants from your community.\n\nIt’s not just fun, it will bring your people in fast.`,
        slug: 'bestyearever',
      },
    },

    3: {
      heroBannerData: {
        title: 'Your Big Purpose',
        label: 'Session 3',
        wistiaVideoId: '0fc4ponvrl',
        shortDescription:
          'Capturing a successful Big Purpose is the difference between a thriving community where members want to contribute and invite other members vs, one where you’re the only one invested and struggling with engagement.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'Your Big Purpose is also your pitch',
        content: (
          <p className='ast-p-element'>
            Your Big Purpose is the reason why your Ideal Members, or the people who need your community the most right
            now, will show up, join, pay, engage, contribute, and build relationships with other members as they move
            towards their Best Year Ever — the results and transformation that they want in their lives.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session3Cover.gatsbyImageData,
        description:
          'There’s 50+ examples to inspire you and guided prompts to help you craft a compelling and motivational Big Purpose.',
        resourceUrl: 'https://docs.google.com/document/d/13wH7LBXpduGGEFxXXKu3TybGc3rnfaHm7FtTBZCP1ns/',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session3Thumbnail.gatsbyImageData,
        description: `YOUR BIG PURPOSE is the only pitch you’ll ever need to bring your IDEAL MEMBERS into your community.\n\nYou’ll be SHOCKED at how easy it is to craft your pitch and just how well it does its job.`,
        slug: 'bigpurpose',
      },
    },

    4: {
      heroBannerData: {
        title: 'Monthly Themes',
        label: 'Session 4',
        wistiaVideoId: '39pbv81fae',
        shortDescription:
          'Doing a tiny bit of planning up front will make it much easier for you to run your community in the months to come. We’ll teach you how.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'Monthly Themes are like a roadmap to results',
        content: (
          <p className='ast-p-element'>
            Monthly Themes create novelty in your community. Everything feels fresh and motivating—and never repetitive.
            This builds ongoing momentum and energy that leads to the results and transformation that creates your
            members Best Year Ever. You’ll leave this session with inspirational examples and a simple way to brainstorm
            a full year of Monthly Themes.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session4Cover.gatsbyImageData,
        description:
          'You’ll have 3 simple prompts that will help you brainstorm your Monthly Themes and several examples to learn from.',
        resourceUrl: 'https://docs.google.com/document/d/1XOEBpSHjCHyvgZZAwkwb83w2HB0pyFtnVyOBBixbAAE/edit',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session4Thumbnail.gatsbyImageData,
        description: `You may be wondering what your IDEAL MEMBERS are going to DO in your community?\n\nYour Community Design Plan starts with MONTHLY THEMES.`,
        slug: 'monthlythemes',
      },
    },

    5: {
      heroBannerData: {
        title: 'Weekly Calendar',
        label: 'Session 5',
        wistiaVideoId: '67oi865bp8',
        shortDescription:
          'Each community has its own rhythm. We’ll teach you how to define yours—without relying on constant content creation.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'A Weekly Calendar builds a habit',
        content: (
          <p className='ast-p-element'>
            Your Weekly Calendar will create the habit for your members to come back to your community every week,
            multiple times to contribute. The best part? It’s not actually about filling the space with content. You’ll
            leave this session with confidence that you can create steady engagement with 2-3 lightweight rituals a
            week.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session5Cover.gatsbyImageData,
        description:
          'It has a guided brainstorm for your Weekly Calendar and some killer examples and ideas to inspire.',
        resourceUrl: 'https://docs.google.com/document/d/1xEzAUsn1TAK5s1iKGwPDwig5-BA5_XVkjKxg8kn-Zg4/edit',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session5Thumbnail.gatsbyImageData,
        description: `Next up, your membership will quickly become a habit with a simple WEEKLY CALENDAR.\n\nWe’ll give you a blueprint—and it won’t require you to be the star of the show.`,
        slug: 'weeklycalendar',
      },
    },

    6: {
      heroBannerData: {
        title: 'Daily Polls & Questions',
        label: 'Session 6',
        wistiaVideoId: '166e46saiw',
        shortDescription:
          'Questions help people contribute and connect in a lighthearted and natural way. We’ll teach you the simple formula that generates an infinite supply.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'Great questions are the key to killer engagement',
        content: (
          <p className='ast-p-element'>
            Our data is clear—questions are the best way to quickly seed conversations among your members. Conservations
            which go on to spur deep connections. You’ll leave this session with the two ingredients that make up every
            question and a plan to generate hundreds for your community.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session6Cover.gatsbyImageData,
        description:
          'Master the simple question formula with some prompts and then swipe our library of 1000+ great community questions.',
        resourceUrl:
          'https://docs.google.com/document/d/1RRp0HqEpxe_HCKxCvkzxUYU176-IYWJD4RMv5cNN9Fw/edit#heading=h.qwt0okbqwg07',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session6Thumbnail.gatsbyImageData,
        description: `Finally, we’re going to give you the secret to extraordinary engagement with no work.\n\nIt starts with a certain flavor of DAILY POLLS & QUESTIONS. And we’ll unveil an endless supply of new ones. `,
        slug: 'dailypollsandquestions',
      },
    },

    7: {
      heroBannerData: {
        title: 'Your Offer',
        label: 'Session 7',
        wistiaVideoId: 'uh6imv7l6n',
        shortDescription:
          'How much are members willing to pay to achieve their results and transformation? Hint: It’s a lot more than you think. Learn how to position and price your community with confidence.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'People pay attention to what they pay for',
        content: (
          <p className='ast-p-element'>
            Our data is shockingly clear: Paid communities generate higher engagement and faster results than free (or
            freemium) communities. You should feel confident that your offer is going to deliver the Best Year Ever.
            You’ll leave this session understanding how to make that crystal clear for any prospective Ideal Members.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session7Cover.gatsbyImageData,
        description:
          'You’ll get the formula that will help you price your offer and a worksheet to guide you through the process.',
        resourceUrl: 'https://docs.google.com/document/d/1SZNUqWKNYSm-ZQIjLKoc696dPIRMWsvIPxT18lQi8jU/edit',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session7Thumbnail.gatsbyImageData,
        description: `We’re in the homestretch with Your OFFER.\n\nGina will show you how to package up everything above into Your OFFER—and the exact formula to choose a price.`,
        slug: 'offer',
      },
    },

    8: {
      heroBannerData: {
        title: 'Your Launch',
        label: 'Session 8',
        wistiaVideoId: 'smtheawpr3',
        shortDescription:
          'You don’t need perfect videos, a sophisticated SEO strategy, or paid ads to launch your community. You do need a tried and true formula that we’re happy to share.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'Your Launch formula is simpler than you think',
        content: (
          <p className='ast-p-element'>
            You don’t have to start with a huge audience or following to have a successful launch. We’ll teach you how
            to start with your network and create a ripple effect that attracts your Ideal Member. You’ll leave this
            session with a straightforward launch plan and clarity on who to target.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session8Cover.gatsbyImageData,
        description: 'Simple actions and email scripts that will help you launch smoothly and quickly. ',
        resourceUrl: 'https://docs.google.com/document/d/1ynwSNMfIkYZPebJPYJkVwsjd7uZd2V4zPsyHk31Tyw8/edit',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session8Thumbnail.gatsbyImageData,
        description: `For some, LAUNCHES can be intimidating. Not with this proven LAUNCH formula.\n\nWe’ll take all of the guesswork out of what you need to launch and grow.`,
        slug: 'launch',
      },
    },

    9: {
      heroBannerData: {
        title: 'A Magical First Experience',
        label: 'Session 9',
        wistiaVideoId: '8nxhfz5mil',
        shortDescription:
          '99% of community entrepreneurs miss out on delivering a magical first experience. You’re not going to be one of them.',
      },
      articleBodyData: {
        label: 'Summary',
        title: 'A Magical First Experience leads with value',
        content: (
          <p className='ast-p-element'>
            Purpose, connection, and surprise are the three elements that make up a magical first experience. The
            problem? Community builders often overlook them and jump right into the mechanics. We’ll make sure you lead
            with value—not logistics—and craft a memorable first experience that has members wanting more.
          </p>
        ),
      },
      getTheGuideData: {
        guideThumbnail: session9Cover.gatsbyImageData,
        description:
          'Steal 50 ideas for a Magical First Experience that make it feel unexpected, memorable, and incredibly exciting.',
        resourceUrl: 'https://docs.google.com/document/d/1wp_OF5uhpCUaDBLvpz9bq6DFMRqD-gcuqoPIP8kZ4ao/edit',
      },
      allSessionsData: {
        title: 'All Sessions',
        thumbnail: session9Thumbnail.gatsbyImageData,
        description: `Our 9 STEPS end with a MAGICAL FIRST EXPERIENCE for your members.\n\nThis isn’t about mechanics. It’s about starting with VALUE and wins. We’ll show you exactly what to do.`,
        slug: 'magicalfirstexperience',
      },
    },
  };
};

export default useMasterclassData;
